import { useMemo } from 'react';
import { GlobalOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { languageOptions } from '@app/constants';
import { changeLanguage, showDefaultErrorToast } from '@app/utils';
import { useRefreshSession, useUpdateUserDetails } from '@data/hooks';
import { useAppUserStore } from '@data/storage';
import { Language } from '@data/utils/enums';
import { AntdDropdown } from '@ui/components';
import { NavBarDropdownTrigger } from '../NavBarDropdownTrigger';

export const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAppUserStore();

  const { mutate: updateUserDetails, isPending } = useUpdateUserDetails();
  const { mutate: refreshSession } = useRefreshSession();

  const currentLanguage = i18n.resolvedLanguage ?? i18n.language;

  // Options only — menuItems must stay unmemoized so handleSelect keeps closing over the
  // current user, which arrives after first render on the optimistic sign-up flow.
  const options = useMemo(() => languageOptions(), [i18n.language]);
  const currentOption = options.find((option) => option.value === currentLanguage);

  const handleSelect = (language: Language) => {
    if (language === currentLanguage) return;

    changeLanguage(language, i18n);

    // No session yet (optimistic sign-up) — an unauthenticated PATCH would log the user out.
    if (!user) return;

    // Persist, since the profile language also drives emails and the next token.
    updateUserDetails(
      { language },
      {
        onSuccess: () =>
          refreshSession(
            // Read the token fresh: the axios interceptor may have rotated it while the PATCH
            // was in flight.
            { refreshToken: useAppUserStore.getState().user?.refreshToken ?? '' },
            { onError: showDefaultErrorToast },
          ),
        onError: (error) => {
          changeLanguage(currentLanguage, i18n);
          showDefaultErrorToast(error);
        },
      },
    );
  };

  const menuItems: MenuProps['items'] = options.map((option) => ({
    key: option.value,
    label: option.longLabel,
    onClick: () => handleSelect(option.value),
  }));

  return (
    <AntdDropdown
      menu={{ items: menuItems, selectedKeys: [currentLanguage] }}
      trigger={['click']}
      disabled={isPending}
    >
      <NavBarDropdownTrigger
        icon={GlobalOutlined}
        label={currentOption?.longLabel}
        aria-label={t('common.language')}
        data-testid="language-switcher"
      />
    </AntdDropdown>
  );
};
